import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import { collectBackingMixingRecord, savesectionApicall, fetchCycleData, type CycleData } from '../Services/BackingMixingRecord';
import { addOfflineData } from '../store/BackingMixingSlice';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';

const BACKING_MIXING_INITIAL_KEY = 'backingMixingInitialData';

interface BackingMixingInitialData {
  productName: string;
  executiveName: string;
  lineNo: string;
  standardMixingTime?: string;
}

function getBackingMixingInitial(): BackingMixingInitialData | null {
  try {
    const s = localStorage.getItem(BACKING_MIXING_INITIAL_KEY);
    return s ? (JSON.parse(s) as BackingMixingInitialData) : null;
  } catch {
    return null;
  }
}

function saveBackingMixingInitial(data: BackingMixingInitialData): void {
  try {
    localStorage.setItem(BACKING_MIXING_INITIAL_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save Backing Mixing initial data', e);
  }
}

function formatTime(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

/** Parse comma-separated Dividing Weight (10 readings). Returns array of 10 trimmed values or null if invalid. */
function parseDividingWeight10(value: string): { valid: boolean; readings: string[]; message?: string } {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return { valid: false, readings: [], message: 'Dividing Weight (10 Readings) is required. Enter exactly 10 values separated by commas.' };
  }
  const readings = trimmed.split(',').map(s => s.trim()).filter(Boolean);
  if (readings.length < 10) {
    return { valid: false, readings, message: `Minimum 10 readings required. You entered ${readings.length}. Please enter 10 values separated by commas (e.g. 1,2,2,2,2,2,2,2,2,2).` };
  }
  if (readings.length > 10) {
    return { valid: false, readings, message: `Maximum 10 readings allowed. You entered ${readings.length}. Please enter exactly 10 values separated by commas.` };
  }
  return { valid: true, readings };
}

const MAX_CYCLES = 100;

interface CycleProductData {
  cycleNum: string;
  product: string;
  executiveName: string;
  lineNo: string;
  startTime: string;
  standardMixingTime: string;
  mixingTime: string;
  mixingOutTime: string;
  waterTemp: string;
  yeastSlurryTemp: string;
  oilTemp: string;
  iceKg: string;
  doughTemp: string;
  doughPh: string;
  dividingWeight10: string;
  seedVisibility: string;
}

const emptyStartForm = { productName: '', executiveName: '', lineNo: '', standardMixingTime: '' };
const emptySaveForm = {
  mixingTime: '',
  mixingOutTime: '',
  waterTemp: '',
  yeastSlurryTemp: '',
  oilTemp: '',
  iceKg: '',
  doughTemp: '',
  doughPh: '',
  dividingWeight10: '',
  seedVisibility: '',
};

const BackingMixing: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
  const user = useSelector((state: RootState) => state.user?.user);
  const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
  const reduxFetchedCycles = useSelector((state: RootState) => state.backingMixing?.fetchedCycles ?? []);

  const idx = (cycle: number) => Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));

  const offlineInitial = useMemo(() => {
    try {
      const s = store.getState();
      if (!s?.appState?.isOfflineStarted) return null;
      const fetched = s?.backingMixing?.fetchedCycles ?? [];
      if (!Array.isArray(fetched) || fetched.length === 0) return null;
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      const idxFn = (c: number) => Math.max(0, Math.min(c - 1, MAX_CYCLES - 1));
      fetched.forEach((cycleData: any) => {
        let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idxFn(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNo: cycleData.lineNo || '',
            startTime: cycleData.startTime || '',
            standardMixingTime: cycleData.standardMixingTime || '',
            mixingTime: cycleData.mixingTime || '',
            mixingOutTime: cycleData.mixingOutTime || '',
            waterTemp: cycleData.waterTemp || '',
            yeastSlurryTemp: cycleData.yeastSlurryTemp || '',
            oilTemp: cycleData.oilTemp || '',
            iceKg: cycleData.iceKg || '',
            doughTemp: cycleData.doughTemp || '',
            doughPh: cycleData.doughPh || '',
            dividingWeight10: cycleData.dividingWeight10 || '',
            seedVisibility: cycleData.seedVisibility || '',
          });
        }
      });
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
      return { savedProducts: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
    } catch {
      return null;
    }
  }, []);

  const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(() => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false));
  const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [savedProducts, setSavedProducts] = useState<CycleProductData[][]>(() => offlineInitial?.savedProducts ?? Array(MAX_CYCLES).fill(null).map(() => []));
  const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm })));
  const [saveFormDataByCycle, setSaveFormDataByCycle] = useState<typeof emptySaveForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptySaveForm })));
  const [startTimeByCycle, setStartTimeByCycle] = useState<string[]>(() => Array(MAX_CYCLES).fill(''));
  const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
  const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());

  const setCycleCompletedAt = (cycle: number, value: boolean) => setCycleCompleted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setIsSessionStartedAt = (cycle: number, value: boolean) => setIsSessionStarted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setCycleExpandedAt = (cycle: number, value: boolean) => setCycleExpanded(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setSavedProductsAt = (cycle: number, updater: (prev: CycleProductData[]) => CycleProductData[]) => setSavedProducts(prev => { const n = prev.map(a => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });
  const setShowStartFormForNewProductAt = (cycle: number, value: boolean) => setShowStartFormForNewProduct(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) => setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setSaveFormDataByCycleAt = (cycle: number, value: typeof emptySaveForm) => setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setStartTimeAt = (cycle: number, value: string) => setStartTimeByCycle(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, cycle: number = 1, isSaveForm: boolean = false) => {
    const { name, value } = e.target;
    const i = idx(cycle);
    if (isSaveForm) {
      setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
    } else {
      setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
    }
  };

  const handleStartSession = (cycle: number = 1) => {
    const data = formDataByCycle[idx(cycle)];
    saveBackingMixingInitial({
      productName: data.productName || '',
      executiveName: data.executiveName || '',
      lineNo: data.lineNo || '',
      standardMixingTime: data.standardMixingTime || '',
    });
    setShowStartFormForNewProductAt(cycle, false);
    setStartTimeAt(cycle, formatTime(new Date()));
    setIsSessionStartedAt(cycle, true);
  };

  const handleCancelSession = (cycle: number = 1) => setIsSessionStartedAt(cycle, false);

  const handleSaveSession = async (cycle: number = 1) => {
    if (!plantTourId) {
      alert('Plant Tour ID is missing. Please start a plant tour first.');
      return;
    }
    const i = idx(cycle);
    const saveData = saveFormDataByCycle[i];
    const startData = formDataByCycle[i];
    const userName = user?.Name || null;
    const productName = (startData.productName || '').trim() || 'N/A';
    const executiveName = (startData.executiveName || '').trim() || 'N/A';
    const lineNo = (startData.lineNo || '').trim() || null;
    const startTime = startTimeByCycle[i] || formatTime(new Date());

    // Validate Dividing Weight (10 Readings): exactly 10 comma-separated values required
    const dividingWeightInput = (saveData.dividingWeight10 || '').trim();
    const dividingWeightResult = parseDividingWeight10(dividingWeightInput);
    if (!dividingWeightResult.valid) {
      alert(dividingWeightResult.message);
      return;
    }
    const dividingWeight10Value = dividingWeightResult.readings.join(',');

    const standardMixingTimeValue = (startData.standardMixingTime || '').trim() || null;

    const record = collectBackingMixingRecord(
      cycle,
      productName,
      executiveName,
      lineNo,
      startTime,
      standardMixingTimeValue,
      (saveData.mixingTime || '').trim() || null,
      (saveData.mixingOutTime || '').trim() || null,
      (saveData.waterTemp || '').trim() || null,
      (saveData.yeastSlurryTemp || '').trim() || null,
      (saveData.oilTemp || '').trim() || null,
      (saveData.iceKg || '').trim() || null,
      (saveData.doughTemp || '').trim() || null,
      (saveData.doughPh || '').trim() || null,
      dividingWeight10Value || null,
      (saveData.seedVisibility || '').trim() || null,
      plantTourId,
      userName
    );

    const savedRecord: CycleProductData = {
      cycleNum: String(cycle),
      product: productName,
      executiveName,
      lineNo: lineNo || '',
      startTime,
      standardMixingTime: standardMixingTimeValue || '',
      mixingTime: saveData.mixingTime || '',
      mixingOutTime: saveData.mixingOutTime || '',
      waterTemp: saveData.waterTemp || '',
      yeastSlurryTemp: saveData.yeastSlurryTemp || '',
      oilTemp: saveData.oilTemp || '',
      iceKg: saveData.iceKg || '',
      doughTemp: saveData.doughTemp || '',
      doughPh: saveData.doughPh || '',
      dividingWeight10: dividingWeight10Value || saveData.dividingWeight10 || '',
      seedVisibility: saveData.seedVisibility || '',
    };

    try {
      if (isOfflineStarted) {
        dispatch(addOfflineData({
          cycleNo: cycle,
          records: [savedRecord],
          timestamp: Date.now(),
        }));
        setSavedProductsAt(cycle, prev => [...prev, savedRecord]);
        setIsSessionStartedAt(cycle, false);
        setShowStartFormForNewProductAt(cycle, false);
        try {
          const key = `backing-mixing-cycle-${cycle}-data`;
          const existing = localStorage.getItem(key);
          const existingArr = existing ? (JSON.parse(existing) as any[]) : [];
          const merged = Array.isArray(existingArr) ? [...existingArr, record] : [record];
          localStorage.setItem(key, JSON.stringify(merged));
        } catch (_) {}
        showOfflineSaveAlertForCategory(cycle, 'Backing Mixing');
      } else {
        const result = await savesectionApicall([record]);
        if (result.success) {
          setSavedProductsAt(cycle, prev => [...prev, savedRecord]);
          setIsSessionStartedAt(cycle, false);
          setShowStartFormForNewProductAt(cycle, false);
        } else {
          alert(`Failed to save Cycle ${cycle}: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save session.'}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const handleCompleteCycle = (cycle: number) => {
    const products = savedProducts[idx(cycle)];
    if (!products.length) {
      alert('Please save at least one product before completing the cycle.');
      return;
    }
    setCycleCompletedAt(cycle, true);
    setIsSessionStartedAt(cycle, false);
    if (cycle === currentCycle) setCurrentCycle(prev => prev + 1);
  };

  const toggleCompletedCycleExpansion = (cycleNum: number) => {
    setExpandedCompletedCycles(prev => {
      const next = new Set(prev);
      if (next.has(cycleNum)) next.delete(cycleNum);
      else next.add(cycleNum);
      return next;
    });
  };

  const loadCycleData = async () => {
    if (!plantTourId || isOfflineStarted) return;
    try {
      const cycles = await fetchCycleData(plantTourId);
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cycles.forEach((cycleData: CycleData) => {
        const cycleNum = parseInt(cycleData.cycleNum, 10);
        const i = idx(cycleNum);
        if (i < 0 || i >= MAX_CYCLES) return;
        byCycle[i].push({
          cycleNum: cycleData.cycleNum,
          product: cycleData.product !== 'N/A' ? cycleData.product : '',
          executiveName: cycleData.executiveName !== 'N/A' ? cycleData.executiveName : '',
          lineNo: cycleData.lineNo || '',
          startTime: cycleData.startTime || '',
          standardMixingTime: cycleData.standardMixingTime || '',
          mixingTime: cycleData.mixingTime || '',
          mixingOutTime: cycleData.mixingOutTime || '',
          waterTemp: cycleData.waterTemp || '',
          yeastSlurryTemp: cycleData.yeastSlurryTemp || '',
          oilTemp: cycleData.oilTemp || '',
          iceKg: cycleData.iceKg || '',
          doughTemp: cycleData.doughTemp || '',
          doughPh: cycleData.doughPh || '',
          dividingWeight10: cycleData.dividingWeight10 || '',
          seedVisibility: cycleData.seedVisibility || '',
        });
      });
      setSavedProducts(byCycle);
      setCycleCompleted(prev => prev.map((v, i) => (byCycle[i].length > 0 ? true : v)));
      const maxCycleFromData = cycles.length ? Math.max(...cycles.map(cd => parseInt(String(cd.cycleNum), 10) || 0), 1) : 0;
      setCurrentCycle(maxCycleFromData + 1);
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  useLayoutEffect(() => {
    if (!plantTourId || !isOfflineStarted) return;
    const fetched = store.getState()?.backingMixing?.fetchedCycles ?? [];
    if (!Array.isArray(fetched) || fetched.length === 0) return;
    const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
    fetched.forEach((cycleData: any) => {
      let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
      const cycleNum = parseInt(cycleNumStr, 10);
      if (isNaN(cycleNum) || cycleNum < 1) return;
      const i = idx(cycleNum);
      if (i >= 0 && i < MAX_CYCLES) {
        byCycle[i].push({
          cycleNum: String(cycleNum),
          product: cycleData.product || '',
          executiveName: cycleData.executiveName || '',
          lineNo: cycleData.lineNo || '',
          startTime: cycleData.startTime || '',
          standardMixingTime: cycleData.standardMixingTime || '',
          mixingTime: cycleData.mixingTime || '',
          mixingOutTime: cycleData.mixingOutTime || '',
          waterTemp: cycleData.waterTemp || '',
          yeastSlurryTemp: cycleData.yeastSlurryTemp || '',
          oilTemp: cycleData.oilTemp || '',
          iceKg: cycleData.iceKg || '',
          doughTemp: cycleData.doughTemp || '',
          doughPh: cycleData.doughPh || '',
          dividingWeight10: cycleData.dividingWeight10 || '',
          seedVisibility: cycleData.seedVisibility || '',
        });
      }
    });
    const completedCycles = Array(MAX_CYCLES).fill(false);
    byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
    const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
    setSavedProducts(byCycle);
    setCycleCompleted(completedCycles);
    setCurrentCycle(maxCycleFromData + 1);
  }, [plantTourId, isOfflineStarted]);

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const cyclesToUse = store.getState()?.backingMixing?.fetchedCycles ?? [];
      if (!Array.isArray(cyclesToUse) || cyclesToUse.length === 0) {
        setSavedProducts(Array(MAX_CYCLES).fill(null).map(() => []));
        setCycleCompleted(Array(MAX_CYCLES).fill(false));
        setCurrentCycle(1);
        return;
      }
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cyclesToUse.forEach((cycleData: any) => {
        let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            lineNo: cycleData.lineNo || '',
            startTime: cycleData.startTime || '',
            standardMixingTime: cycleData.standardMixingTime || '',
            mixingTime: cycleData.mixingTime || '',
            mixingOutTime: cycleData.mixingOutTime || '',
            waterTemp: cycleData.waterTemp || '',
            yeastSlurryTemp: cycleData.yeastSlurryTemp || '',
            oilTemp: cycleData.oilTemp || '',
            iceKg: cycleData.iceKg || '',
            doughTemp: cycleData.doughTemp || '',
            doughPh: cycleData.doughPh || '',
            dividingWeight10: cycleData.dividingWeight10 || '',
            seedVisibility: cycleData.seedVisibility || '',
          });
        }
      });
      setSavedProducts(byCycle);
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
      setCurrentCycle(maxCycleFromData + 1);
      return;
    }
    loadCycleData();
  }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

  useEffect(() => {
    const initial = getBackingMixingInitial();
    if (!initial?.productName && !initial?.executiveName && !initial?.lineNo) return;
    const std = initial.standardMixingTime ?? '';
    setFormDataByCycle(prev => prev.map((p, i) => {
      const cycleNum = i + 1;
      if (cycleNum === 1) {
        if (p.productName || p.executiveName || p.lineNo) return p;
        return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '', lineNo: initial?.lineNo ?? '', standardMixingTime: std };
      }
      if (!cycleCompleted[cycleNum - 2]) return p;
      if (p.productName || p.executiveName || p.lineNo) return p;
      return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '', lineNo: initial?.lineNo ?? '', standardMixingTime: std };
    }));
  }, [cycleCompleted]);

  const renderSummaryTable = (productData: CycleProductData) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border border-gray-200">
        <thead>
          <tr className="bg-gray-50">
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Field</th>
            <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Value</th>
          </tr>
        </thead>
        <tbody>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Standard Mixing Time</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.standardMixingTime || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Mixing – In Time</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.mixingTime || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Mixing- Out Time</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.mixingOutTime || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Water Temp</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.waterTemp || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Yeast Slurry Temp</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.yeastSlurryTemp || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Oil Temp</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.oilTemp || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Ice (kg)</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.iceKg || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Dough Temp</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.doughTemp || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Dough pH</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.doughPh || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Dividing Weight (10)</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.dividingWeight10 || '-'}</td></tr>
          <tr><td className="border border-gray-200 px-3 py-2 text-gray-700">Seed Visibility</td><td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.seedVisibility || '-'}</td></tr>
        </tbody>
      </table>
    </div>
  );

  const shiftValue = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('shiftValue') || 'Shift 1' : 'Shift 1';
  const headerDate = (() => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
  })();

  return (
    <DashboardLayout>
      <div className="w-full px-4 md:px-6 py-6">
        <div className="mb-4">
          <button
            type="button"
            onClick={handleBack}
            className="flex items-center gap-1 text-gray-800 hover:text-gray-600 font-semibold bg-transparent border-0 cursor-pointer p-0"
          >
            <span className="text-lg">←</span>
            <span>Back</span>
          </button>
        </div>
        <div className="flex items-center justify-between px-4 py-3 mb-6 rounded-lg bg-gray-100 border border-gray-200">
          <h1 className="text-xl font-bold text-gray-800">Mixing</h1>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-200 text-gray-800 text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
              {shiftValue}
            </span>
            <span className="text-gray-500">•</span>
            <span className="text-sm text-gray-800">{headerDate}</span>
            <button
              type="button"
              className="p-1 rounded hover:bg-gray-200 text-gray-600"
              aria-label="More options"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 space-y-6">
          {/* Completed cycles */}
          {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1)
            .filter(n => cycleCompleted[idx(n)] || savedProducts[idx(n)].length > 0)
            .map(n => (
              <div key={n} className="light-surface bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer border-b border-gray-200"
                  onClick={() => toggleCompletedCycleExpansion(n)}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {n}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {expandedCompletedCycles.has(n) && (
                  <div className="p-4 space-y-6">
                    {savedProducts[idx(n)].map((productData, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-red-50 rounded-t-lg px-4 py-2">
                          <h3 className="text-sm font-bold text-red-700">Cycle Summary</h3>
                        </div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Product:</span> {productData.product || 'N/A'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Executive:</span> {productData.executiveName || 'N/A'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Line No:</span> {productData.lineNo || '-'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Time:</span> {productData.startTime || '-'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Standard Mixing Time:</span> {productData.standardMixingTime || '-'}</p>
                        </div>
                        {renderSummaryTable(productData)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Current cycle card */}
          <div className="light-surface bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {!cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && (
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Cycle {currentCycle}</h2>
              </div>
            )}

            {/* Start section: Product Name, Executive Name, Line No, Standard Mixing Time, Start Session */}
            {!isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (savedProducts[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                    <input
                      type="text"
                      name="productName"
                      value={formDataByCycle[idx(currentCycle)].productName}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Executive Name</label>
                    <input
                      type="text"
                      name="executiveName"
                      value={formDataByCycle[idx(currentCycle)].executiveName}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Line No</label>
                    <input
                      type="text"
                      name="lineNo"
                      value={formDataByCycle[idx(currentCycle)].lineNo}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Standard Mixing Time</label>
                    <input
                      type="time"
                      name="standardMixingTime"
                      value={formDataByCycle[idx(currentCycle)].standardMixingTime}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => handleStartSession(currentCycle)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Start Session
                  </button>
                </div>
              </div>
            )}

            {/* Completed current cycle - show summary */}
            {cycleCompleted[idx(currentCycle)] && (
              <>
                <div
                  className="flex items-center justify-between mb-4 cursor-pointer"
                  onClick={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 ${cycleExpanded[idx(currentCycle)] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {cycleExpanded[idx(currentCycle)] && (
                  <div className="space-y-6">
                    {savedProducts[idx(currentCycle)].map((productData, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-red-50 rounded-t-lg px-4 py-2">
                          <h3 className="text-sm font-bold text-red-700">Cycle Summary</h3>
                        </div>
                        <div className="border border-t-0 border-gray-200 rounded-b-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Product:</span> {productData.product || 'N/A'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Executive:</span> {productData.executiveName || 'N/A'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Line No:</span> {productData.lineNo || '-'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Time:</span> {productData.startTime || '-'}</p>
                          <p className="text-sm font-semibold text-gray-800"><span className="text-gray-600">Standard Mixing Time:</span> {productData.standardMixingTime || '-'}</p>
                        </div>
                        {renderSummaryTable(productData)}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* After save: Add new product / Complete Cycle */}
            {savedProducts[idx(currentCycle)].length > 0 && !cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && !showStartFormForNewProduct[idx(currentCycle)] && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-semibold mb-3">
                    {savedProducts[idx(currentCycle)].length} product(s) saved in Cycle {currentCycle}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowStartFormForNewProductAt(currentCycle, true);
                        setFormDataByCycleAt(currentCycle, {
                          productName: getBackingMixingInitial()?.productName ?? '',
                          executiveName: getBackingMixingInitial()?.executiveName ?? '',
                          lineNo: getBackingMixingInitial()?.lineNo ?? '',
                          standardMixingTime: getBackingMixingInitial()?.standardMixingTime ?? '',
                        });
                        setSaveFormDataByCycleAt(currentCycle, { ...emptySaveForm });
                        setStartTimeAt(currentCycle, formatTime(new Date()));
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                    >
                      Add new product
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCompleteCycle(currentCycle)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                    >
                      Complete Cycle & Move to Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Save section: Info bar + Mixing Observations + Cancel / Save Session */}
            {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Product</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].productName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].executiveName || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Line No</div>
                      <div className="font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].lineNo || '-'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Time</div>
                      <div className="font-semibold text-gray-800">{startTimeByCycle[idx(currentCycle)] || '--:--'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Standard Mixing Time</div>
                      <div className="font-semibold text-gray-800">{(formDataByCycle[idx(currentCycle)].standardMixingTime || '').trim() || '-'}</div>
                    </div>
                  </div>
                </div>
                <h3 className="text-base font-bold text-gray-800 mb-4">Mixing Observations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mixing – In Time</label>
                      <input
                        type="time"
                        name="mixingTime"
                        value={saveFormDataByCycle[idx(currentCycle)].mixingTime}
                        onChange={e => handleInputChange(e, currentCycle, true)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mixing- Out Time</label>
                      <input
                        type="time"
                        name="mixingOutTime"
                        value={saveFormDataByCycle[idx(currentCycle)].mixingOutTime}
                        onChange={e => handleInputChange(e, currentCycle, true)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Water Temp</label>
                    <input
                      type="text"
                      name="waterTemp"
                      value={saveFormDataByCycle[idx(currentCycle)].waterTemp}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Yeast Slurry Temp</label>
                    <input
                      type="text"
                      name="yeastSlurryTemp"
                      value={saveFormDataByCycle[idx(currentCycle)].yeastSlurryTemp}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Oil Temp</label>
                    <input
                      type="text"
                      name="oilTemp"
                      value={saveFormDataByCycle[idx(currentCycle)].oilTemp}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ice (kg)</label>
                    <input
                      type="text"
                      name="iceKg"
                      value={saveFormDataByCycle[idx(currentCycle)].iceKg}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dough Temp</label>
                    <input
                      type="text"
                      name="doughTemp"
                      value={saveFormDataByCycle[idx(currentCycle)].doughTemp}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dough pH</label>
                    <input
                      type="text"
                      name="doughPh"
                      value={saveFormDataByCycle[idx(currentCycle)].doughPh}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dividing Weight (10 Readings)</label>
                    <input
                      type="text"
                      name="dividingWeight10"
                      value={saveFormDataByCycle[idx(currentCycle)].dividingWeight10}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g. 1,2,2,2,2,2,2,2,2,2"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter exactly 10 values separated by commas (min 10, max 10 readings required).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Seed Visibility</label>
                    <input
                      type="text"
                      name="seedVisibility"
                      value={saveFormDataByCycle[idx(currentCycle)].seedVisibility}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => handleCancelSession(currentCycle)}
                    className="px-6 py-2 border border-blue-300 text-blue-600 bg-white rounded-md hover:bg-blue-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveSession(currentCycle)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Save Session
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BackingMixing;
